import { createReducer, on } from '@ngrx/store';
import {
  IFileUploadError,
} from '../../models/fileupload.model';
import { FileUploadActions } from './file-upload.actions';

export interface FileUploadItem {
  requestId: string;
  fileName: string;
  fileSize: number;
  key?: string;
  presignedUrl?: string;
  progress: number;
  status: 'pending' | 'generating-url' | 'uploading' | 'completed' | 'failed';
  error?: IFileUploadError;
  uploadedAt?: Date;
}

export interface FileUploadState {
  uploads: { [requestId: string]: FileUploadItem };
  activeUploads: string[]; // Array of requestIds currently being processed
  loading: boolean;
  error: IFileUploadError | null;
  lastUploadedFile?: FileUploadItem;
}

export const initialFileUploadState: FileUploadState = {
  uploads: {},
  activeUploads: [],
  loading: false,
  error: null,
};

export const fileUploadReducer = createReducer(
  initialFileUploadState,

  // Generate Presigned URL
  on(FileUploadActions.generatePresignedUrl, (state, { file }) => {
    const requestId = Date.now().toString(); // This will be replaced with actual requestId from response
    const newUpload: FileUploadItem = {
      requestId,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: 'generating-url',
    };

    return {
      ...state,
      uploads: {
        ...state.uploads,
        [requestId]: newUpload,
      },
      activeUploads: [...state.activeUploads, requestId],
      loading: true,
      error: null,
    };
  }),

  on(
    FileUploadActions.generatePresignedUrlSuccess,
    (state, { response, file }) => {
      const tempRequestId = state.activeUploads.at(-1);
      const updatedUploads = { ...state.uploads };

      // Remove temp entry and create new one with actual requestId
      if (tempRequestId && updatedUploads[tempRequestId]) {
        delete updatedUploads[tempRequestId];
      }

      const newUpload: FileUploadItem = {
        requestId: response.requestId,
        fileName: file.name,
        fileSize: file.size,
        key: response.key,
        presignedUrl: response.url,
        progress: 0,
        status: 'pending',
      };

      return {
        ...state,
        uploads: {
          ...updatedUploads,
          [response.requestId]: newUpload,
        },
        activeUploads: state.activeUploads
          .filter((id) => id !== tempRequestId)
          .concat(response.requestId),
        loading: false,
        error: null,
      };
    }
  ),

  on(FileUploadActions.generatePresignedUrlFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Upload File to S3
  on(FileUploadActions.uploadFileToS3, (state, { requestId }) => ({
    ...state,
    uploads: {
      ...state.uploads,
      [requestId]: {
        ...state.uploads[requestId],
        status: 'uploading' as const,
      },
    },
    loading: true,
  })),

  on(FileUploadActions.uploadFileToS3Progress, (state, { progress }) => ({
    ...state,
    uploads: {
      ...state.uploads,
      [progress.requestId]: {
        ...state.uploads[progress.requestId],
        progress: progress.progress,
        status: progress.status,
      },
    },
  })),

  on(FileUploadActions.uploadFileToS3Success, (state, { requestId }) => ({
    ...state,
    uploads: {
      ...state.uploads,
      [requestId]: {
        ...state.uploads[requestId],
        progress: 100,
        status: 'completed' as const,
        uploadedAt: new Date(),
      },
    },
    activeUploads: state.activeUploads.filter((id) => id !== requestId),
    loading: state.activeUploads.length <= 1,
    lastUploadedFile: state.uploads[requestId],
  })),

  on(FileUploadActions.uploadFileToS3Failure, (state, { error }) => {
    const requestId = error.requestId;
    if (!requestId) return { ...state, loading: false, error };

    return {
      ...state,
      uploads: {
        ...state.uploads,
        [requestId]: {
          ...state.uploads[requestId],
          status: 'failed' as const,
          error,
        },
      },
      activeUploads: state.activeUploads.filter((id) => id !== requestId),
      loading: state.activeUploads.length <= 1,
      error,
    };
  }),

  // Complete Upload Process
  on(FileUploadActions.uploadFile, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(
    FileUploadActions.uploadFileSuccess,
    (state, { requestId }) => ({
      ...state,
      uploads: {
        ...state.uploads,
        [requestId]: {
          ...state.uploads[requestId],
          progress: 100,
          status: 'completed' as const,
          uploadedAt: new Date(),
        },
      },
      activeUploads: state.activeUploads.filter((id) => id !== requestId),
      loading: state.activeUploads.length <= 1,
      lastUploadedFile: state.uploads[requestId],
    })
  ),

  on(FileUploadActions.uploadFileFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Clear Actions
  on(FileUploadActions.clearUploadError, (state, { requestId }) => {
    if (requestId) {
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [requestId]: {
            ...state.uploads[requestId],
            error: undefined,
          },
        },
      };
    }
    return {
      ...state,
      error: null,
    };
  }),

  on(FileUploadActions.clearUploadHistory, (state) => ({
    ...state,
    uploads: {},
  })),

  on(FileUploadActions.resetUploadState, () => initialFileUploadState)
);
